import { App, AutoComplete, Button, Checkbox, Col, Form, Input, InputNumber, Row, Select, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { addOfferWorkPlaceItem, updateEquipmentTab, updateField } from '../../../../../contextRedux/Offer/OfferModel';
import { SearchInput } from '../../../../../customComponents/input/SearchInput';
import { CustomTable } from '../../../../../customComponents/table/CustomTable';
import { RootState } from '../../../../../store';
import { getAvailablePeriods } from '../../../../../utils/contractUtils';

const { Option } = AutoComplete;

const AddEquipmentTab: FC<any> = ({ searchCompanies }) => {
  const isMobile = useMediaQuery({ maxWidth: 720 });

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchTable, setSearchTable] = useState<string>('');
  const [checkAll, setCheckAll] = useState(false); // "Select All" checkbox state
  const [indeterminate, setIndeterminate] = useState(false);
  const [searchRecomandationPieceNumber, setRecomandationPieceNumber] = useState<number>(1);

  const offer = useSelector((state: RootState) => state.models.offer);
  const systemTypes = useSelector((state: RootState) => state.store.systemType.data);
  const selectedSystemTypeId = useSelector((state: RootState) => state.models.offer.equipmentTab.systemTypeId);
  const deviceTypes = useSelector((state: RootState) => state.store.deviceType.data);
  const [filtredDeviceTypes, setFiltredDeviceTypes] = useState<any[]>(
    useSelector((state: RootState) =>
      state.store.deviceType.data.filter((d: any) => d.systemTypeId == state.models.offer.equipmentTab.systemTypeId)
    )
  );

  const systemTypeId = useSelector((state: RootState) => state.models.offer.equipmentTab.systemTypeId);
  const deviceTypeData = useSelector((state: RootState) => state.store.deviceType.data);

  useEffect(() => {
    setFiltredDeviceTypes(deviceTypeData.filter((d: any) => d.systemTypeId === systemTypeId));
  }, [systemTypeId, deviceTypeData]);

  const addedDevices = offer.offerWorkPlaceItems;
  const workPlaces = useSelector((state: RootState) => state.models.offer.company?.workPlaces);
  const agents = useSelector((state: RootState) => state.models.offer.company.contactPersons ?? []);

  const typePeriod = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];

  // Get available periods based on contract duration
  const availablePeriods = getAvailablePeriods(offer.dateStart, offer.dateEnd);

  const handleAddDevice = async (record: any, isFromWorkPlaceItems: boolean = false) => {
    const locations = offer.location; // Array of locations in the contract
    const systemType = form.getFieldValue('systemType');
    const interventionPerSystemType = form.getFieldValue('interventionPerSystemType') ?? 3;
    const period = form.getFieldValue('period');

    let isErrorMessageShown = false;
    let isFilteredErrorMessageShown = false; // New flag for filteredAddedDevice
    let isSuccessMessageShown = false; // Flag to show the success message only once

    for (const location of locations) {
      // Check if the device is already added for this location
      const isDeviceAlreadyAddedForLocation = addedDevices?.some(
        (ad: any) => ad.deviceTypeId === record.id && ad.workPlaceId === location.id
      );

      if (isDeviceAlreadyAddedForLocation) {
        if (!isErrorMessageShown) {
          message.error(<span>{record?.name} este deja adaugat la această locație!</span>);
          isErrorMessageShown = true; // Prevent further error messages for this device
        }
        continue; // Skip this location if the device is already added
      }

      // Check for filteredAddedDevice condition
      const filteredAddedDevice = addedDevices?.filter(
        (ad: any) =>
          ad.workPlace?.name === location?.name &&
          ad.systemType?.name === systemType?.name &&
          ad.interventionPerSystemType !== interventionPerSystemType &&
          ad.period !== period
      );

      if (filteredAddedDevice?.length > 0) {
        if (!isFilteredErrorMessageShown) {
          message.error(
            <span>
              Nu puteti adauga alt device pe aceeași locație: <b>{filteredAddedDevice[0]?.workPlace?.name}</b>, cu
              același sistem: <b>{filteredAddedDevice[0].systemType?.name}</b> cu o periodicitate diferită de{' '}
              <b>{filteredAddedDevice[0].period}</b> sau un număr de intervenții diferit de{' '}
              <b>{filteredAddedDevice[0].interventionPerSystemType}</b>
            </span>,
            20
          );
          isFilteredErrorMessageShown = true; // Prevent further error messages for filteredAddedDevice
        }
        continue; // Skip this location if the filtered condition is met
      }

      const workPlaceItem = location?.workPlaceItems?.find((wpi: any) => wpi?.deviceTypeId === record?.id);
      const pieceCount = workPlaceItem?.piece ?? 1; // Default to 1 if piece is not found

      const deviceData = {
        ...record,
        workPlace: location,
        workPlaceId: location?.id,
        device: { ...record },
        deviceTypeId: record.id,
        agent: offer?.equipmentTab?.agent ?? form.getFieldValue('agent'),
        agentId: offer?.equipmentTab?.agentId ?? form.getFieldValue('agentId'),
        systemType: systemType,
        systemTypeId: systemType?.id,
        metadata: {
          deviceType: { price: record?.price },
        },
        details: offer.equipmentTab.details,
        period: period || 'lunar',
        interventionPerSystemType,
        unitMeasure: period == 'la cerere' ? 'hour' : 'buc',
        laborPrice: period == 'la cerere' ? offer.equipmentTab.interventionPriceOnRequest ?? 300 : record?.laborPriceSM,
        laborTime: period == 'la cerere' ? 60 : record?.laborTimeSM * pieceCount,
        piece: pieceCount,
        interventionPriceOnRequest: offer.equipmentTab.interventionPriceOnRequest ?? 300,
      };

      if (isFromWorkPlaceItems) {
        dispatch(addOfferWorkPlaceItem(deviceData));
      } else {
        form.setFieldsValue({
          ...record,
          details: offer.equipmentTab.details,
          laborTimeSMTotal: record?.totalLaborsTimeSM,
          laborPriceSMTotal: record?.totalLaborsPriceSM,
          unitMeasure: period == 'la cerere' ? 'hour' : 'buc',
          interventionPriceOnRequest: offer.equipmentTab.interventionPriceOnRequest ?? 300,
          piece: searchRecomandationPieceNumber ?? 1,
        });

        const newItem = {
          id: '',
          contractWorkPlaceId: '',
          workPlace: location,
          workPlaceId: location?.id,
          agent: offer?.equipmentTab?.agent ?? form.getFieldValue('agent'),
          agentId: offer?.equipmentTab?.agentId ?? form.getFieldValue('agent')?.id,
          systemType: offer.equipmentTab.systemType,
          systemTypeId: offer.equipmentTab.systemTypeId,
          device: { ...record },
          deviceTypeId: record.id,
          contractWorkPlaceMetadata: {},
          period: offer.equipmentTab.period,
          laborPrice: record.laborPriceSM,
          laborTime: record.laborTimeSM,
          unitMeasure: record.unitMeasure ?? 'buc',
          isAnotherProvider: record.isAnotherProvider ?? false,
          interventionPerSystemType: offer.equipmentTab.interventionPerSystemType,
          interventionPriceOnRequest: offer.equipmentTab.interventionPriceOnRequest ?? 300,
          addition: record.addition,
          piece: searchRecomandationPieceNumber ?? 1,
          metadata: { deviceType: { price: record.price } },
          price: Math.round(
            record.price * (searchRecomandationPieceNumber ?? 1) +
              ((record.price * record.addition) / 100) * (searchRecomandationPieceNumber ?? 1)
          ),
          details: offer.equipmentTab.details,
        };

        dispatch(addOfferWorkPlaceItem(newItem));
      }

      // Show success message only once
      if (!isSuccessMessageShown) {
        message.success(
          <span>
            <span className='font-weight-bold'>{record?.name}</span> a fost adăugat.
          </span>
        );
        isSuccessMessageShown = true;
      }
    }
  };

  const handleSelectAgent = (value: string) => {
    const agent = agents.find((awp: any) => awp?.name == value);

    if (agent) {
      const fieldsValue = {
        ...form.getFieldsValue(true),
        agent: agent,
      };
      form.setFieldsValue(fieldsValue);
      dispatch(updateEquipmentTab({ field: 'agentId', value: agent?.id ?? null }));
      dispatch(updateEquipmentTab({ field: 'agent', value: agent ?? null }));
    }
  };

  const handleSystemType = (value: string) => {
    const systemType = systemTypes.find((st: any) => st.name == value);

    if (systemType) {
      const fieldsValue = {
        ...form.getFieldsValue(true),
        systemType: systemType,
        systemTypeId: systemType?.id,
      };
      form.setFieldsValue(fieldsValue);
      dispatch(updateEquipmentTab({ field: 'systemTypeId', value: systemType?.id ?? null }));
      dispatch(updateEquipmentTab({ field: 'systemType', value: systemType ?? null }));

      setFiltredDeviceTypes(deviceTypes.filter((dt: any) => dt.systemTypeId == systemType.id));
    }
  };

  const handleSelectPeriod = (value: string) => {
    const period = typePeriod.find((period: any) => period == value);

    dispatch(updateEquipmentTab({ field: 'period', value: period ?? null }));
  };

  const handleChangeInterventionPriceOnRequest = (value: number | null) => {
    if (value !== null) {
      dispatch(updateEquipmentTab({ field: 'interventionPriceOnRequest', value: value ?? 300 }));
    }
  };

  const handleChangeDetails = (value: any) => {
    dispatch(updateEquipmentTab({ field: 'details', value: value }));
  };

  const handleSearchTermChange = (value: string, inputName?: string) => {
    if (
      (inputName == 'location' &&
        searchTerm != '' &&
        searchTerm.toLocaleLowerCase() === 'Locatie Noua'.toLocaleLowerCase()) ||
      (searchTerm.toLocaleLowerCase().includes(value) && value != '')
    ) {
      searchCompanies();
    }
    setSearchTerm(value);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const handleExistentSystemType = async (systemName: string) => {
    const systemType = systemTypes.find((st: any) => st.name === systemName);
    const isOnRequestPeriod =
      addedDevices.some((ad: any) => ad.period == 'la cerere') || offer.equipmentTab.period == 'la cerere';

    // Iterate through all locations in the offer
    offer.location?.forEach((location: any) => {
      const locationItems =
        location?.workPlaceItems?.filter((workPlaceItem: any) => workPlaceItem?.systemTypeId === systemType?.id) || [];

      locationItems.forEach((workPlaceItem: any) => {
        const findedDevice = {
          ...deviceTypes.find((dt: any) => dt.id === workPlaceItem?.deviceTypeId),
          isAnotherProvider: workPlaceItem?.isAnotherProvider ?? false,
        };

        // Check if the device is already added for this location
        const isDeviceAlreadyAdded = addedDevices.some(
          (ad: any) => ad.deviceTypeId === findedDevice.id && ad.workPlaceId === location.id
        );

        if (isDeviceAlreadyAdded) {
          // If the device is already in addedDevices, show error message for this specific device
          message.error(
            <span>
              The device <strong>{findedDevice.name}</strong> is already added at location{' '}
              <strong>{location.name}</strong>.
            </span>,
            5
          );
          return; // Skip adding this device
        }

        // Prepare device data for dispatch
        const pieceCount = workPlaceItem?.piece ?? 1;
        const deviceData = {
          ...findedDevice,
          workPlace: location,
          workPlaceId: location.id,
          device: { ...findedDevice },
          deviceTypeId: findedDevice.id,
          agent: offer.equipmentTab.agent ?? location?.contactPersons[0],
          agentId: offer.equipmentTab.agentId ?? location?.contactPersons[0]?.id,
          systemType: systemType,
          systemTypeId: systemType?.id,
          metadata: {
            deviceType: { price: findedDevice?.price },
          },
          details: offer.equipmentTab.details,
          period: offer.equipmentTab.period || 'lunar',
          unitMeasure: isOnRequestPeriod ? 'hour' : 'buc',
          laborPrice: isOnRequestPeriod
            ? offer.equipmentTab.interventionPriceOnRequest ?? 300
            : findedDevice?.laborPriceSM!,
          laborTime: isOnRequestPeriod ? 60 : findedDevice?.laborTimeSM! * pieceCount,
          piece: pieceCount,
          interventionPriceOnRequest: offer.equipmentTab.interventionPriceOnRequest ?? 300,
        };

        // Dispatch the device data
        dispatch(addOfferWorkPlaceItem(deviceData));

        // Show success message for this specific device
        message.success(
          <span>
            The device <strong>{findedDevice.name}</strong> has been successfully added at location{' '}
            <strong>{location.name}</strong>.
          </span>,
          5
        );
      });
    });
  };

  const handleSearchInDeviceTable = (value: string, recomandationPieceNumber: number) => {
    setSearchTable(value);

    setRecomandationPieceNumber(recomandationPieceNumber);
    dispatch(updateEquipmentTab({ field: 'interventionPerSystemType', value: recomandationPieceNumber }));
    const filtered = deviceTypes
      ?.filter((device: any) => device.systemTypeId === selectedSystemTypeId)
      .filter(
        (device: any) =>
          (searchRecomandationPieceNumber >= device?.minRecomandation &&
            searchRecomandationPieceNumber <= device?.maxRecomandation &&
            device.name.toLowerCase().includes(value.toLowerCase())) ||
          device.model.toLowerCase().includes(value.toLowerCase()) ||
          device.producer.toLowerCase().includes(value.toLowerCase()) ||
          device.provider.toLowerCase().includes(value.toLowerCase()) ||
          device.price.toString().toLowerCase().includes(value.toLowerCase()) ||
          device.laborTimeSM.toString().toLowerCase().includes(value.toLowerCase()) ||
          device.laborPriceSM.toString().toLowerCase().includes(value.toLowerCase())
      );

    setFiltredDeviceTypes(filtered);
  };

  const handleLocationChange = (value: any) => {
    const selectedLocations = workPlaces.filter((w: any) => value.includes(w.name));
    dispatch(updateField({ field: 'location', value: selectedLocations }));
    updateSelectAllState(value);
  };

  const handleSelectAllChange = (e: any) => {
    const { checked } = e.target;
    const selectedLocations = checked ? workPlaces : [];
    dispatch(updateField({ field: 'location', value: selectedLocations }));
    setCheckAll(checked);
    setIndeterminate(false);
  };

  const updateSelectAllState = (selected: any) => {
    if (selected.length === workPlaces?.length) {
      setCheckAll(true);
      setIndeterminate(false);
    } else if (selected.length > 0) {
      setCheckAll(false);
      setIndeterminate(true);
    } else {
      setCheckAll(false);
      setIndeterminate(false);
    }
  };

  const deviceTableColumns: any = [
    {
      title: 'Device Id',
      dataIndex: 'id',
      key: 'id',
      hidden: true,
    },
    {
      title: 'System Type Id',
      dataIndex: 'systemTypeId',
      key: 'systemTypeId',
      hidden: true,
    },
    {
      title: 'Imagine',
      dataIndex: 'imageLink',
      key: 'imageLink',
      render: (record: any) =>
        record[0] ? <img src={record[0]} alt='No Image' style={{ width: '100px', height: 'auto' }} /> : 'No image',
    },
    {
      title: 'Denumire',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Tooltip title={text}>
          <div className='ellipsis-cell'>{text}</div>
        </Tooltip>
      ),
    },
    {
      title: 'System Type',
      dataIndex: ['systemType', 'name'],
      key: 'systemTypeName',
      width: 150,
    },
    {
      title: 'Pret',
      dataIndex: 'price',
      key: 'price',
    },
    {
      title: 'Timp Manopera SM (Minute)',
      dataIndex: 'laborTimeSM',
      key: 'laborTimeSM',
      width: 200,
    },
    {
      title: 'Pret Manopera SM',
      dataIndex: 'laborPriceSM',
      key: 'laborPriceSM',
      width: 200,
    },
    {
      title: 'Producator',
      dataIndex: 'producer',
      key: 'producer',
    },
    {
      title: 'Furnizor',
      dataIndex: 'provider',
      key: 'provider',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Actiune',
      dataIndex: '',
      key: 'actions',
      fixed: 'right',
      render: (record: any) => {
        return (
          <Button
            onClick={() => handleAddDevice(record)}
            disabled={
              !(
                offer?.location?.length > 0 &&
                offer?.equipmentTab?.agentId != undefined &&
                offer?.equipmentTab?.systemTypeId != undefined &&
                offer?.equipmentTab?.period != undefined
              )
            }
            style={{ background: '#1677ff', color: 'white' }}
          >
            Adauga
          </Button>
        );
      },
      width: 10,
    },
  ];

  // Card rendering
  const renderCards = (filtredDeviceTypes: any, handleAddDevice: (device: any) => void, offer: any) => (
    <div className='cardContainer'>
      {filtredDeviceTypes.map((device: any) => (
        <div className='device-card' key={device.id}>
          <img
            src={device.imageLink && device.imageLink[0] ? device.imageLink[0] : ''}
            alt='Device'
            className='cardImage'
          />
          <div className='cardContent'>
            <p>
              <strong>Denumire:</strong> {device.name}
            </p>
            <p>
              <strong>Model:</strong> {device.model}
            </p>
            <p>
              <strong>Pret:</strong> {device.price}
            </p>
          </div>
          <button
            className='addButton'
            onClick={() => handleAddDevice(device)}
            disabled={
              !(
                offer?.location?.length > 0 &&
                offer?.equipmentTab?.agentId !== undefined &&
                offer?.equipmentTab?.systemTypeId !== undefined &&
                offer?.equipmentTab?.period !== undefined
              )
            }
          >
            Adauga
          </button>
        </div>
      ))}
    </div>
  );

  // Table rendering
  const renderTable = () => (
    <CustomTable initialDataSource={filtredDeviceTypes} columns={deviceTableColumns} rowKey='id' />
  );

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        className='form-container'
        initialValues={{ interventionPerSystemType: offer.equipmentTab.interventionPerSystemType ?? 3 }}
      >
        <Row gutter={[16, 1]}>
          <Col xs={24} sm={12} md={12} lg={8}>
            <Form.Item name={['location', 'name']} label='Locatii'>
              <div>
                <Select
                  mode='multiple'
                  allowClear
                  placeholder='Selecteaza locatie'
                  value={offer.location?.map((loc: any) => loc?.name)} // Ensure the value is an array of strings
                  onChange={handleLocationChange}
                  filterOption={handleFilter}
                  className='add-equipment-select-input'
                >
                  <Option key='select-all' disabled>
                    <Checkbox indeterminate={indeterminate} onChange={handleSelectAllChange} checked={checkAll}>
                      Select All
                    </Checkbox>
                  </Option>
                  {workPlaces
                    ?.filter((location: any) => location?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()))
                    .map((location: any, index: any) => (
                      <Option key={index} value={location?.name}>
                        {location?.name}
                      </Option>
                    ))}
                </Select>
              </div>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={12} lg={8}>
            <Form.Item name={['agent', 'name']} label='Persoana de contact'>
              <AutoComplete
                placeholder='Selectati persoana de contact'
                onSelect={handleSelectAgent}
                onChange={() => handleSearchTermChange}
                filterOption={handleFilter}
                value={searchTerm}
              >
                {agents?.map((a: any, index: any) => (
                  <Option key={index} value={a?.name}>
                    {a?.name} - {a?.role}
                  </Option>
                ))}
              </AutoComplete>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={12} lg={8}>
            <Form.Item name='period' label='Periodicitate'>
              <AutoComplete
                placeholder='Selectati perioada'
                onSelect={handleSelectPeriod}
                onChange={() => handleSearchTermChange}
              >
                {addedDevices?.length <= 0
                  ? availablePeriods.map((item: any, idx: any) => (
                      <Option key={idx} value={item}>
                        {item}
                      </Option>
                    ))
                  : addedDevices?.length > 0 && addedDevices.some((ad: any) => ad.period == 'la cerere')
                  ? ['la cerere']
                      ?.filter((period) => availablePeriods.includes(period))
                      .map((item, idx) => (
                        <Option key={idx} value={item}>
                          {item}
                        </Option>
                      ))
                  : availablePeriods
                      ?.filter((t: any) => t != 'la cerere')
                      .map((item: any, idx: any) => (
                        <Option key={idx} value={item}>
                          {item}
                        </Option>
                      ))}
              </AutoComplete>
            </Form.Item>
          </Col>

          {offer?.equipmentTab?.period === 'la cerere' && (
            <Col xs={24} sm={12} md={12} lg={8}>
              <Form.Item name='interventionPriceOnRequest' label='Pret interventie la cerere' initialValue={300}>
                <InputNumber
                  onChange={handleChangeInterventionPriceOnRequest}
                  style={{ width: '100%' }}
                  placeholder='Introduceti pretul'
                  min={1}
                />
              </Form.Item>
            </Col>
          )}

          {offer?.location?.length > 0 && (
            <Col xs={24} sm={12} md={12} lg={8}>
              <Form.Item name='details' label='Detalii situare sistem'>
                <Input placeholder='Ex: Etaj 2, camera A2' onChange={(e) => handleChangeDetails(e.target.value)} />
              </Form.Item>
            </Col>
          )}

          {offer?.location?.length > 0 && (
            <Col xs={24} sm={12} md={12} lg={8}>
              <Form.Item
                name='existentSystem'
                label={`Tipuri de sistem existente pe ${
                  offer.location.length > 0 ? 'locatiile selectate' : 'locatia selectata'
                }`}
              >
                <AutoComplete placeholder='Selectati sistemul' onSelect={handleExistentSystemType}>
                  {Array.from(
                    new Map(
                      offer.location
                        ?.flatMap((loc: any) => loc.workPlaceItems) // Flatten all workPlaceItems
                        ?.map((item: any) => [
                          item.systemTypeId, // Key by systemTypeId
                          systemTypes.find((st: any) => st.id === item.systemTypeId)?.name, // Map to systemTypeName
                        ])
                    ).values() // Extract unique values
                  )
                    ?.filter(Boolean) // Remove undefined/null
                    ?.map((systemTypeName: any, index: number) => (
                      <Option key={index} value={systemTypeName}>
                        {systemTypeName}
                      </Option>
                    ))}
                </AutoComplete>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={12} lg={8}>
            <Form.Item name={['systemType', 'name']} label='Tipuri de sistem'>
              <AutoComplete
                placeholder='Selectati sistemul'
                onSelect={handleSystemType}
                onChange={() => handleSearchTermChange}
                filterOption={handleFilter}
                value={searchTerm}
              >
                {systemTypes?.map((systemType: any, index: any) => (
                  <Option key={index} value={systemType.name}>
                    {systemType.name}
                  </Option>
                ))}
              </AutoComplete>
            </Form.Item>
          </Col>
        </Row>

        {/* <Form.Item name='interventionPerSystemType' label='Interventii per sistem'>
          <InputNumber style={{ width: '100%' }} placeholder='Ex: 3' min={1} />
        </Form.Item> */}
        {
          deviceTypes?.length > 0 ? (
            <div>
              <div>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={12} md={12} lg={12}>
                    <InputNumber
                      placeholder='Introduceti numarul de bucati'
                      value={searchRecomandationPieceNumber}
                      min={1}
                      onChange={(value) => handleSearchInDeviceTable(searchTable, value ?? 1)}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <SearchInput
                      placeholder='Search'
                      value={searchTable}
                      onChange={(e) => handleSearchInDeviceTable(e.target.value, searchRecomandationPieceNumber)}
                      autoFocus={true}
                    />
                  </Col>
                </Row>
              </div>

              <div>{isMobile ? renderCards(filtredDeviceTypes, handleAddDevice, offer) : renderTable()}</div>
            </div>
          ) : null
          //selectedSystemType && <h3>Not found devices for this system type!</h3>
        }
      </Form>
    </>
  );
};

export default AddEquipmentTab;
